import { InfiniteData, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAuthContext } from '@/context/AuthProvider';
import { useWebSocketContext } from '@/context/WebSocketProvider';
import { useLogger } from '@/hooks/useLogger';
import { ChatMessageStatus } from '@/models/ChatMessage';
import {
  ChatAttachment,
  GetGroupMessagesResponse,
  GroupChatMessage,
} from '@/models/GroupChatMessage';
import { Mention } from '@/models/Mention';
import { CognitoGroup } from '@/models/User';
import { getGroupChatMessages, markGroupAsRead } from '@/services/api/clientGroupChat';

const DELETE_REMOVE_DELAY_MS = 60_000;

const getSenderRole = (role: CognitoGroup): string => {
  switch (role) {
    case CognitoGroup.Advisor:
      return 'Advisor';
    case CognitoGroup.SuperAdmin:
    case CognitoGroup.Admin:
      return 'Admin';
    default:
      return 'Buyer';
  }
};

export const useClientGroupChat = (groupId?: string) => {
  const logger = useLogger('useClientGroupChat');
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const {
    lastGroupChatMessage,
    lastGroupDeletedMessage,
    lastGroupEditedMessage,
    lastGroupReactionMessage,
    sendWebSocketMessage,
    connectionAttempt,
  } = useWebSocketContext();
  const lastProcessedMessageRef = useRef<string | null>(null);
  const lastProcessedDeleteRef = useRef<string | null>(null);
  const lastProcessedEditRef = useRef<string | null>(null);
  const lastProcessedReactionRef = useRef<string | null>(null);
  const deleteTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const lastSyncedAttemptRef = useRef(0);

  const scheduleDeleteRemoval = useCallback(
    (targetGroupId: string, createdAt: number) => {
      if (deleteTimersRef.current.has(createdAt)) return;
      const timer = setTimeout(() => {
        deleteTimersRef.current.delete(createdAt);
        queryClient.setQueryData<InfiniteData<GetGroupMessagesResponse>>(
          ['groupChatMessages', targetGroupId],
          (oldData) => {
            if (!oldData?.pages?.length) return oldData;
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                messages: page.messages.filter((msg) => msg.createdAt !== createdAt),
              })),
            };
          },
        );
      }, DELETE_REMOVE_DELAY_MS);
      deleteTimersRef.current.set(createdAt, timer);
    },
    [queryClient],
  );

  useEffect(
    () => () => {
      deleteTimersRef.current.forEach((timer) => clearTimeout(timer));
      deleteTimersRef.current.clear();
    },
    [],
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingMessages,
  } = useInfiniteQuery({
    queryKey: ['groupChatMessages', groupId],
    queryFn: ({ pageParam }) => getGroupChatMessages(groupId ?? '', pageParam),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(groupId),
    staleTime: 30_000,
    select: (data) => {
      const allMessages = data.pages.flatMap((page) => page.messages);
      return allMessages.reverse();
    },
  });

  const messages = useMemo(() => data ?? [], [data]);

  const { mutate: markAsRead } = useMutation({
    mutationFn: () => markGroupAsRead(groupId ?? ''),
    onMutate: () => {
      queryClient.setQueryData<{
        groups: { groupId: string; unreadCount: number }[];
        totalUnread: number;
      }>(['groupUnreadCounts'], (oldData) => {
        if (!oldData) return oldData;
        const updated = oldData.groups.map((g) =>
          g.groupId === groupId ? { ...g, unreadCount: 0 } : g,
        );
        const totalUnread = updated.reduce((sum, g) => sum + g.unreadCount, 0);
        return { ...oldData, groups: updated, totalUnread };
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupUnreadCounts'] });
    },
    onError: (error) => {
      logger.error('Error marking group as read:', error);
      queryClient.invalidateQueries({ queryKey: ['groupUnreadCounts'] });
    },
  });

  const sendMessage = (
    content: string,
    mentions?: Mention[],
    attachments?: ChatAttachment[],
  ): boolean => {
    if (!groupId || !user) {
      logger.warn('Cannot send message: missing groupId or user');
      return false;
    }
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const optimisticMessage: GroupChatMessage = {
      groupId,
      createdAt: Date.now(),
      messageId: tempId,
      userId: user.id,
      content,
      status: ChatMessageStatus.Pending,
      senderName: `${user.firstName} ${user.lastName}`,
      senderRole: getSenderRole(user.roles?.[0] ?? CognitoGroup.Buyer),
      ...(mentions?.length ? { mentions } : {}),
      ...(attachments?.length ? { attachments } : {}),
    };
    queryClient.setQueryData<InfiniteData<GetGroupMessagesResponse>>(
      ['groupChatMessages', groupId],
      (oldData) => {
        if (!oldData?.pages?.length) return oldData;
        const newPages = [...oldData.pages];
        newPages[0] = { ...newPages[0], messages: [optimisticMessage, ...newPages[0].messages] };
        return { ...oldData, pages: newPages };
      },
    );
    const sent = sendWebSocketMessage({
      action: 'sendGroupMessage',
      groupId,
      content,
      clientMessageId: tempId,
      ...(mentions?.length ? { mentions } : {}),
      ...(attachments?.length ? { attachments } : {}),
    });
    if (!sent) {
      // Revert optimistic update — WebSocket is disconnected
      queryClient.setQueryData<InfiniteData<GetGroupMessagesResponse>>(
        ['groupChatMessages', groupId],
        (oldData) => {
          if (!oldData?.pages?.length) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              messages: page.messages.filter((msg) => msg.messageId !== tempId),
            })),
          };
        },
      );
      logger.warn('Message reverted: WebSocket disconnected');
      return false;
    }
    logger.debug('Sent group message via WebSocket:', { groupId, content });
    return true;
  };

  const sendDeleteMessage = (createdAt: number) => {
    if (!groupId) return;
    queryClient.setQueryData<InfiniteData<GetGroupMessagesResponse>>(
      ['groupChatMessages', groupId],
      (oldData) => {
        if (!oldData?.pages?.length) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.createdAt === createdAt
                ? { ...msg, status: ChatMessageStatus.Deleted, content: '' }
                : msg,
            ),
          })),
        };
      },
    );
    sendWebSocketMessage({ action: 'deleteGroupMessage', groupId, createdAt });
    scheduleDeleteRemoval(groupId, createdAt);
  };

  const sendEditMessage = (createdAt: number, newContent: string) => {
    if (!groupId) return;
    const editedAt = Date.now();
    queryClient.setQueryData<InfiniteData<GetGroupMessagesResponse>>(
      ['groupChatMessages', groupId],
      (oldData) => {
        if (!oldData?.pages?.length) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.createdAt === createdAt
                ? { ...msg, content: newContent, edited: true, editedAt }
                : msg,
            ),
          })),
        };
      },
    );
    sendWebSocketMessage({ action: 'editGroupMessage', groupId, createdAt, content: newContent });
  };

  const sendReaction = (createdAt: number, emoji: string, action: 'add' | 'remove') => {
    if (!groupId || !user) return;
    const userName = `${user.firstName} ${user.lastName}`;
    queryClient.setQueryData<InfiniteData<GetGroupMessagesResponse>>(
      ['groupChatMessages', groupId],
      (oldData) => {
        if (!oldData?.pages?.length) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) => {
              if (msg.createdAt !== createdAt) return msg;
              const reactions = msg.reactions || [];
              if (action === 'add') {
                if (reactions.some((r) => r.userId === user.id && r.emoji === emoji)) return msg;
                return { ...msg, reactions: [...reactions, { userId: user.id, emoji, userName }] };
              } else {
                return {
                  ...msg,
                  reactions: reactions.filter((r) => !(r.userId === user.id && r.emoji === emoji)),
                };
              }
            }),
          })),
        };
      },
    );
    sendWebSocketMessage({
      action: 'reactToGroupMessage',
      groupId,
      createdAt,
      emoji,
      reactionAction: action,
    });
  };

  // Invalidate all cached group chat queries on WebSocket reconnect so missed
  // messages are refetched. Active (mounted) queries refetch immediately;
  // inactive ones are marked stale and refetch on next mount.
  useEffect(() => {
    if (connectionAttempt < 2) return;
    if (connectionAttempt === lastSyncedAttemptRef.current) return;
    lastSyncedAttemptRef.current = connectionAttempt;
    queryClient.invalidateQueries({ queryKey: ['groupChatMessages'] });
  }, [connectionAttempt, queryClient]);

  // Handle incoming NEW_GROUP_MESSAGE
  useEffect(() => {
    if (!lastGroupChatMessage || !groupId) return;
    const { groupId: msgGroupId, data: newMessage } = lastGroupChatMessage;
    if (lastProcessedMessageRef.current === newMessage.messageId) return;
    lastProcessedMessageRef.current = newMessage.messageId;
    if (msgGroupId !== groupId) return;
    logger.debug('Received NEW_GROUP_MESSAGE via WebSocket:', newMessage);
    queryClient.setQueryData<InfiniteData<GetGroupMessagesResponse>>(
      ['groupChatMessages', groupId],
      (oldData) => {
        if (!oldData?.pages?.length) return oldData;

        // Primary reconciliation: match via clientMessageId, which the server
        // echoes back verbatim. This does NOT require userId formats to match
        // between the auth context and the backend, which was the root cause of
        // the duplicate-message bug (temp stayed + echo inserted as new).
        const echoedClientId = newMessage.clientMessageId;
        if (echoedClientId) {
          let reconciled = false;
          const reconciledPages = oldData.pages.map((page) => {
            if (reconciled) return page;
            const idx = page.messages.findIndex((m) => m.messageId === echoedClientId);
            if (idx === -1) return page;
            reconciled = true;
            const messages = [...page.messages];
            messages[idx] = newMessage;
            return { ...page, messages };
          });
          if (reconciled) return { ...oldData, pages: reconciledPages };
        }

        // Guard: skip if this message is already in the cache.
        const alreadyExists = oldData.pages.some((p) =>
          p.messages.some(
            (m) => m.messageId === newMessage.messageId || m.messageId === echoedClientId,
          ),
        );
        if (alreadyExists) return oldData;

        // Fallback cleanup: remove at most one orphaned Pending temp- message —
        // the closest createdAt match within 30s across all pages. Removing all
        // matches would wipe out unacknowledged optimistic messages when the
        // user sends several in quick succession.
        const FALLBACK_WINDOW_MS = 30_000;
        let bestMatch: { pageIndex: number; msgIndex: number; delta: number } | undefined;
        oldData.pages.forEach((page, pageIndex) => {
          page.messages.forEach((m, msgIndex) => {
            if (!m.messageId.startsWith('temp-') || m.status !== ChatMessageStatus.Pending) return;
            const delta = Math.abs(m.createdAt - newMessage.createdAt);
            if (delta >= FALLBACK_WINDOW_MS) return;
            if (!bestMatch || delta < bestMatch.delta) bestMatch = { pageIndex, msgIndex, delta };
          });
        });
        const matchToRemove = bestMatch;
        const cleanedPages = oldData.pages.map((page, pageIndex) => {
          if (!matchToRemove || matchToRemove.pageIndex !== pageIndex) return page;
          return {
            ...page,
            messages: page.messages.filter((_, i) => i !== matchToRemove.msgIndex),
          };
        });
        return {
          ...oldData,
          pages: cleanedPages.map((page, index) => {
            if (index !== 0) return page;
            return { ...page, messages: [newMessage, ...page.messages] };
          }),
        };
      },
    );
  }, [lastGroupChatMessage, groupId, queryClient, logger]);

  // Handle MESSAGE_DELETED
  useEffect(() => {
    if (!lastGroupDeletedMessage || !groupId) return;
    const { groupId: msgGroupId, data } = lastGroupDeletedMessage;
    const dedupeKey = `${data.messageId}-${data.createdAt}`;
    if (lastProcessedDeleteRef.current === dedupeKey) return;
    lastProcessedDeleteRef.current = dedupeKey;
    if (msgGroupId !== groupId) return;
    queryClient.setQueryData<InfiniteData<GetGroupMessagesResponse>>(
      ['groupChatMessages', groupId],
      (oldData) => {
        if (!oldData?.pages?.length) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.createdAt === data.createdAt
                ? { ...msg, status: ChatMessageStatus.Deleted, content: '' }
                : msg,
            ),
          })),
        };
      },
    );
    scheduleDeleteRemoval(groupId, data.createdAt);
  }, [lastGroupDeletedMessage, groupId, queryClient, scheduleDeleteRemoval]);

  // Handle MESSAGE_EDITED
  useEffect(() => {
    if (!lastGroupEditedMessage || !groupId) return;
    const { groupId: msgGroupId, data } = lastGroupEditedMessage;
    const dedupeKey = `${data.messageId}-${data.editedAt}`;
    if (lastProcessedEditRef.current === dedupeKey) return;
    lastProcessedEditRef.current = dedupeKey;
    if (msgGroupId !== groupId) return;
    queryClient.setQueryData<InfiniteData<GetGroupMessagesResponse>>(
      ['groupChatMessages', groupId],
      (oldData) => {
        if (!oldData?.pages?.length) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) =>
              msg.createdAt === data.createdAt
                ? { ...msg, content: data.content, edited: true, editedAt: data.editedAt }
                : msg,
            ),
          })),
        };
      },
    );
  }, [lastGroupEditedMessage, groupId, queryClient]);

  // Handle MESSAGE_REACTION
  useEffect(() => {
    if (!lastGroupReactionMessage || !groupId) return;
    const { groupId: msgGroupId, data } = lastGroupReactionMessage;
    const dedupeKey = `${data.createdAt}-${data.userId}-${data.emoji}-${data.action}`;
    if (lastProcessedReactionRef.current === dedupeKey) return;
    lastProcessedReactionRef.current = dedupeKey;
    if (msgGroupId !== groupId) return;
    queryClient.setQueryData<InfiniteData<GetGroupMessagesResponse>>(
      ['groupChatMessages', groupId],
      (oldData) => {
        if (!oldData?.pages?.length) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            messages: page.messages.map((msg) => {
              if (msg.createdAt !== data.createdAt) return msg;
              const reactions = msg.reactions || [];
              if (data.action === 'add') {
                if (reactions.some((r) => r.userId === data.userId && r.emoji === data.emoji))
                  return msg;
                return {
                  ...msg,
                  reactions: [
                    ...reactions,
                    { userId: data.userId, emoji: data.emoji, userName: data.userName },
                  ],
                };
              } else {
                return {
                  ...msg,
                  reactions: reactions.filter(
                    (r) => !(r.userId === data.userId && r.emoji === data.emoji),
                  ),
                };
              }
            }),
          })),
        };
      },
    );
  }, [lastGroupReactionMessage, groupId, queryClient]);

  return {
    messages,
    sendMessage,
    sendDeleteMessage,
    sendEditMessage,
    sendReaction,
    markAsRead,
    isLoadingMessages,
    hasMoreMessages: hasNextPage,
    loadMoreMessages: fetchNextPage,
    isFetchingMore: isFetchingNextPage,
  };
};
